import { useEffect, useMemo, useState } from "react";

import { API_BASE } from "@/config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AssetSearch } from "@/components/ui/asset-search";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getAllowedAssetCategories,
  getAllowedTransactionTypes,
  getDefaultAssetCategory,
  normalizeBookType,
} from "@/lib/book-types";
import { CalendarIcon, Plus, Trash2, Loader2 } from "lucide-react";
import { fetchWithAuth } from "@/lib/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";

import type { Record, Asset, BookType, Fee } from "@/schemas";

type BulkTransactionType =
  | "buy"
  | "sell"
  | "exchange"
  | "currency_conversion"
  | "deposit"
  | "withdrawal"
  | "dividend";

interface TransactionRow {
  id: string;
  date: Date;
  quantity: string;
  secondQuantity: string;
  fee: string;
  feeCurrency: string;
}

interface BulkTransactionModalProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  bookType?: BookType;
  onSubmit: (bookId: string, records: Partial<Record>[]) => Promise<void>;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

const createEmptyRow = (): TransactionRow => ({
  id: generateId(),
  date: new Date(),
  quantity: "",
  secondQuantity: "",
  fee: "",
  feeCurrency: "",
});

const limitDecimals = (value: string): string => {
  if (value === "" || value === ".") return value;
  const parts = value.split(".");
  if (parts.length === 2 && parts[1].length > 8) {
    return `${parts[0]}.${parts[1].slice(0, 8)}`;
  }
  return value;
};

const normalizeDecimalInput = (value: string): string => value.replace(/,/g, ".");

const sanitizeDecimalInput = (value: string): string | null => {
  const normalizedValue = normalizeDecimalInput(value);
  const limitedValue = limitDecimals(normalizedValue);

  if (limitedValue === "" || limitedValue === ".") return limitedValue;
  if (!/^\d*\.?\d*$/.test(limitedValue)) return null;

  return limitedValue;
};

const parseDateInputValue = (value: string): Date | null => {
  if (!value) return null;

  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed;
};

const parsePositive = (value: string): number | null => {
  const parsed = Number.parseFloat(normalizeDecimalInput(value));
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  return parsed;
};

export function BulkTransactionModal({
  open,
  onClose,
  bookId,
  bookType,
  onSubmit,
}: BulkTransactionModalProps) {
  const isMobile = useIsMobile();
  const normalizedBookType = normalizeBookType(bookType);
  const allowedCategories = getAllowedAssetCategories(normalizedBookType);
  const [assetCategory, setAssetCategory] = useState(
    getDefaultAssetCategory(normalizedBookType),
  );
  const [transactionType, setTransactionType] =
    useState<BulkTransactionType>("buy");

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedCounterAsset, setSelectedCounterAsset] =
    useState<Asset | null>(null);
  const [selectedFiat, setSelectedFiat] = useState("");
  const [selectedCashCurrency, setSelectedCashCurrency] = useState("");
  const [selectedCashSourceCurrency, setSelectedCashSourceCurrency] =
    useState("");
  const [selectedCashTargetCurrency, setSelectedCashTargetCurrency] =
    useState("");

  const [fiatOptions, setFiatOptions] = useState<Asset[]>([]);
  const [rows, setRows] = useState<TransactionRow[]>([createEmptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const dialogTitle =
    normalizedBookType === "cash" ? "Bulk Add Cash Flows" : "Bulk Add Transactions";
  const dialogDescription =
    normalizedBookType === "cash"
      ? "Add multiple cash movements in one operation"
      : "Add multiple transactions in one operation";

  useEffect(() => {
    const defaultCategory = getDefaultAssetCategory(normalizedBookType);
    setAssetCategory(defaultCategory);
  }, [normalizedBookType, open]);

  useEffect(() => {
    const allowedTypes = getAllowedTransactionTypes(normalizedBookType).filter(
      (type) => {
        if (assetCategory === "stocks") {
          return type === "buy" || type === "sell" || type === "dividend";
        }
        if (assetCategory === "crypto") {
          return type === "buy" || type === "sell" || type === "exchange";
        }
        return (
          type === "deposit" ||
          type === "withdrawal" ||
          type === "currency_conversion"
        );
      },
    );

    if (allowedTypes.length > 0 && !allowedTypes.includes(transactionType)) {
      setTransactionType(allowedTypes[0]);
    }
  }, [assetCategory, normalizedBookType, transactionType]);

  const usesAmountOnly =
    transactionType === "deposit" ||
    transactionType === "withdrawal" ||
    transactionType === "dividend";

  const quantityLabel = useMemo(() => {
    if (
      transactionType === "deposit" ||
      transactionType === "withdrawal" ||
      transactionType === "dividend"
    ) {
      return "Amount";
    }
    if (transactionType === "exchange") return "Buy Qty";
    if (transactionType === "currency_conversion") return "Buy Amount";
    return "Quantity";
  }, [transactionType]);

  const secondQuantityLabel = useMemo(() => {
    if (transactionType === "exchange") return "Sell Qty";
    if (transactionType === "currency_conversion") return "Sell Amount";
    return "Price/Unit";
  }, [transactionType]);

  useEffect(() => {
    if (!open) return;
    const fetchFiatAssets = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/assets?type=fiat`, {
          method: "GET",
        });
        if (!response.ok) return;
        const data = await response.json();
        setFiatOptions(data.results || []);
      } catch (error) {
        console.error("Error fetching fiat assets:", error);
        setFiatOptions([]);
      }
    };

    void fetchFiatAssets();
  }, [open]);

  useEffect(() => {
    if (!open || fiatOptions.length === 0) return;

    if (!selectedFiat) setSelectedFiat(fiatOptions[0].asset_id);
    if (!selectedCashCurrency) setSelectedCashCurrency(fiatOptions[0].asset_id);
    if (!selectedCashSourceCurrency)
      setSelectedCashSourceCurrency(fiatOptions[0].asset_id);
    if (!selectedCashTargetCurrency)
      setSelectedCashTargetCurrency(fiatOptions[0].asset_id);
  }, [
    open,
    fiatOptions,
    selectedFiat,
    selectedCashCurrency,
    selectedCashSourceCurrency,
    selectedCashTargetCurrency,
  ]);

  useEffect(() => {
    if (
      selectedAsset &&
      "currency" in selectedAsset &&
      selectedAsset.currency
    ) {
      setSelectedFiat(selectedAsset.currency);
    }
  }, [selectedAsset]);

  useEffect(() => {
    if (!open) {
      setAssetCategory("stocks");
      setTransactionType("buy");
      setSelectedAsset(null);
      setSelectedCounterAsset(null);
      setSelectedFiat("");
      setSelectedCashCurrency("");
      setSelectedCashSourceCurrency("");
      setSelectedCashTargetCurrency("");
      setRows([createEmptyRow()]);
      setProgress({ current: 0, total: 0 });
    }
  }, [open]);

  const addRow = () => {
    if (rows.length >= 100) return;
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const updateRow = <K extends keyof TransactionRow>(
    id: string,
    field: K,
    value: TransactionRow[K],
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const isRowValid = (row: TransactionRow) => {
    const quantity = parsePositive(row.quantity);
    if (!quantity) return false;

    if (usesAmountOnly) return true;

    const second = parsePositive(row.secondQuantity);
    return second !== null;
  };

  const getValidRows = () => rows.filter(isRowValid);

  const hasRequiredSelections = () => {
    if (transactionType === "buy" || transactionType === "sell") {
      return !!selectedAsset && !!selectedFiat;
    }
    if (transactionType === "dividend") {
      return !!selectedAsset && !!selectedFiat;
    }
    if (transactionType === "exchange") {
      return !!selectedAsset && !!selectedCounterAsset;
    }
    if (transactionType === "deposit" || transactionType === "withdrawal") {
      return !!selectedCashCurrency;
    }
    if (transactionType === "currency_conversion") {
      return !!selectedCashSourceCurrency && !!selectedCashTargetCurrency;
    }
    return false;
  };

  const isFormValid = () =>
    hasRequiredSelections() && getValidRows().length > 0;

  const buildRecordFromRow = (row: TransactionRow): Partial<Record> | null => {
    const quantity = parsePositive(row.quantity);
    if (!quantity) return null;

    const feeAmount = parsePositive(row.fee);
    const fee: Fee | undefined =
      feeAmount && row.feeCurrency
        ? { asset_id: row.feeCurrency, amount: feeAmount }
        : undefined;

    const baseData: Partial<Record> = {
      type: transactionType,
      record_date: row.date.toISOString(),
      fees: fee ? [fee] : undefined,
    };

    if (transactionType === "buy") {
      const unitPrice = parsePositive(row.secondQuantity);
      if (!selectedAsset || !selectedFiat || !unitPrice) return null;
      return {
        ...baseData,
        asset_id_buy: selectedAsset.asset_id,
        asset_id_sell: selectedFiat,
        qty_buy: quantity,
        qty_sell: quantity * unitPrice,
      };
    }

    if (transactionType === "sell") {
      const unitPrice = parsePositive(row.secondQuantity);
      if (!selectedAsset || !selectedFiat || !unitPrice) return null;
      return {
        ...baseData,
        asset_id_buy: selectedFiat,
        asset_id_sell: selectedAsset.asset_id,
        qty_buy: quantity * unitPrice,
        qty_sell: quantity,
      };
    }

    if (transactionType === "dividend") {
      if (!selectedAsset || !selectedFiat) return null;
      return {
        ...baseData,
        asset_id: selectedAsset.asset_id,
        currency_asset_id: selectedFiat,
        amount: quantity,
      };
    }

    if (transactionType === "exchange") {
      const sellQty = parsePositive(row.secondQuantity);
      if (!selectedAsset || !selectedCounterAsset || !sellQty) return null;
      return {
        ...baseData,
        asset_id_buy: selectedAsset.asset_id,
        asset_id_sell: selectedCounterAsset.asset_id,
        qty_buy: quantity,
        qty_sell: sellQty,
      };
    }

    if (transactionType === "deposit" || transactionType === "withdrawal") {
      if (!selectedCashCurrency) return null;
      return {
        ...baseData,
        asset_id: selectedCashCurrency,
        amount: quantity,
      };
    }

    if (transactionType === "currency_conversion") {
      const sellAmount = parsePositive(row.secondQuantity);
      if (
        !selectedCashSourceCurrency ||
        !selectedCashTargetCurrency ||
        !sellAmount
      )
        return null;
      return {
        ...baseData,
        asset_id_buy: selectedCashTargetCurrency,
        asset_id_sell: selectedCashSourceCurrency,
        qty_buy: quantity,
        qty_sell: sellAmount,
      };
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    const validRows = getValidRows();
    setSubmitting(true);
    setProgress({ current: 0, total: validRows.length });

    try {
      const records = validRows
        .map(buildRecordFromRow)
        .filter((record): record is Partial<Record> => record !== null);

      if (records.length === 0) return;
      await onSubmit(bookId, records);
      setProgress({ current: validRows.length, total: validRows.length });
      onClose();
    } catch (error) {
      console.error("Error submitting transactions:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderAssetSelectors = () => {
    if (assetCategory === "cash") {
      if (transactionType === "currency_conversion") {
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Sell Currency</Label>
              <Select
                value={selectedCashSourceCurrency}
                onValueChange={setSelectedCashSourceCurrency}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source currency" />
                </SelectTrigger>
                <SelectContent>
                  {fiatOptions.map((fiat) => (
                    <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                      {fiat.symbol} - {fiat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Buy Currency</Label>
              <Select
                value={selectedCashTargetCurrency}
                onValueChange={setSelectedCashTargetCurrency}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target currency" />
                </SelectTrigger>
                <SelectContent>
                  {fiatOptions.map((fiat) => (
                    <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                      {fiat.symbol} - {fiat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select
            value={selectedCashCurrency}
            onValueChange={setSelectedCashCurrency}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select currency" />
            </SelectTrigger>
            <SelectContent>
              {fiatOptions.map((fiat) => (
                <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                  {fiat.symbol} - {fiat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {transactionType === "exchange"
              ? "Buy Asset"
              : transactionType === "sell"
                ? "Sell Asset"
                : "Asset"}
          </Label>
          <AssetSearch
            value={selectedAsset?.asset_id}
            onSelect={setSelectedAsset}
            assetType={assetCategory === "stocks" ? "stock,etf,fund" : "crypto"}
            placeholder="Search by name, ticker, or ISIN..."
            enableCompanyFlow={assetCategory === "stocks"}
          />
        </div>

        {transactionType === "exchange" && (
          <div className="space-y-2">
            <Label>Sell Asset</Label>
            <AssetSearch
              value={selectedCounterAsset?.asset_id}
              onSelect={setSelectedCounterAsset}
              assetType="crypto"
              placeholder="Search crypto to sell..."
            />
          </div>
        )}

        {(transactionType === "buy" ||
          transactionType === "sell" ||
          transactionType === "dividend") && (
          <div className="space-y-2">
            <Label>Currency</Label>
            <Select value={selectedFiat} onValueChange={setSelectedFiat}>
              <SelectTrigger>
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {fiatOptions.map((fiat) => (
                  <SelectItem key={fiat.asset_id} value={fiat.asset_id}>
                    {fiat.symbol} - {fiat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {dialogTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {dialogDescription}
          </p>
        </DialogHeader>

        <div className="space-y-4 flex-1 flex flex-col min-h-0">
          <Tabs
            value={assetCategory}
            onValueChange={(value) => {
              const category = value as typeof assetCategory;
              setAssetCategory(category);
              setSelectedAsset(null);
              setSelectedCounterAsset(null);

              if (category === "stocks") setTransactionType("buy");
              if (category === "crypto") setTransactionType("buy");
              if (category === "cash") setTransactionType("deposit");
            }}
          >
            <TabsList variant="line">
              <TabsTrigger
                value="stocks"
                disabled={!allowedCategories.includes("stocks")}
              >
                Stocks/ETFs
              </TabsTrigger>
              <TabsTrigger
                value="crypto"
                disabled={!allowedCategories.includes("crypto")}
              >
                Crypto
              </TabsTrigger>
              <TabsTrigger
                value="cash"
                disabled={!allowedCategories.includes("cash")}
              >
                Cash
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-2">
            <Label>Transaction Type</Label>
            <Select
              value={transactionType}
              onValueChange={(value) =>
                setTransactionType(value as BulkTransactionType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assetCategory === "stocks" &&
                  normalizedBookType !== "cash" && (
                    <>
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "buy",
                      ) && <SelectItem value="buy">Buy</SelectItem>}
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "sell",
                      ) && <SelectItem value="sell">Sell</SelectItem>}
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "dividend",
                      ) && <SelectItem value="dividend">Dividend</SelectItem>}
                    </>
                  )}
                {assetCategory === "crypto" &&
                  normalizedBookType !== "cash" &&
                  normalizedBookType !== "securities" && (
                    <>
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "buy",
                      ) && <SelectItem value="buy">Buy</SelectItem>}
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "sell",
                      ) && <SelectItem value="sell">Sell</SelectItem>}
                      {getAllowedTransactionTypes(normalizedBookType).includes(
                        "exchange",
                      ) && <SelectItem value="exchange">Exchange</SelectItem>}
                    </>
                  )}
                {assetCategory === "cash" && (
                  <>
                    {getAllowedTransactionTypes(normalizedBookType).includes(
                      "deposit",
                    ) && <SelectItem value="deposit">Deposit</SelectItem>}
                    {getAllowedTransactionTypes(normalizedBookType).includes(
                      "withdrawal",
                    ) && <SelectItem value="withdrawal">Withdrawal</SelectItem>}
                    {getAllowedTransactionTypes(normalizedBookType).includes(
                      "currency_conversion",
                    ) && (
                      <SelectItem value="currency_conversion">
                        FX Conversion
                      </SelectItem>
                    )}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {renderAssetSelectors()}

          <div className="space-y-2 flex-1 min-h-0">
            <div className="flex items-center justify-between">
              <Label>
                Transactions ({getValidRows().length} valid
                {rows.length >= 100 ? " / 100 max" : ""})
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRow}
                disabled={rows.length >= 100}
                className="h-8"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Row
              </Button>
            </div>

            <ScrollArea className="h-[280px] border rounded-lg">
              <div className="p-3 space-y-2">
                <div className="hidden grid-cols-12 gap-2 px-1 text-xs font-medium text-muted-foreground md:grid">
                  <div className="col-span-2">Date</div>
                  <div className="col-span-2">{quantityLabel}</div>
                  <div className="col-span-2">{secondQuantityLabel}</div>
                  <div className="col-span-2">Fee</div>
                  <div className="col-span-3">Fee Ccy</div>
                  <div className="col-span-1" />
                </div>

                {rows.map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-1 gap-2 rounded-lg border p-3 md:grid-cols-12 md:items-center md:border-0 md:p-0"
                  >
                    <div className="space-y-1 md:col-span-2 md:space-y-0">
                      {isMobile && (
                        <Label className="text-xs text-muted-foreground md:hidden">
                          Date
                        </Label>
                      )}
                      {isMobile ? (
                        <Input
                          type="date"
                          className="h-10 text-sm md:h-8 md:text-xs"
                          value={format(row.date, "yyyy-MM-dd")}
                          onChange={(event) => {
                            const nextDate = parseDateInputValue(
                              event.target.value,
                            );
                            if (nextDate) {
                              updateRow(row.id, "date", nextDate);
                            }
                          }}
                        />
                      ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className={cn(
                                "w-full h-8 justify-start text-left text-xs",
                                !row.date && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="mr-1 h-3 w-3" />
                              {row.date ? format(row.date, "dd/MM/yy") : "Date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={row.date}
                              onSelect={(date) =>
                                updateRow(row.id, "date", date || new Date())
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>

                    <div className="space-y-1 md:col-span-2 md:space-y-0">
                      {isMobile && (
                        <Label className="text-xs text-muted-foreground md:hidden">
                          {quantityLabel}
                        </Label>
                      )}
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        className="h-10 text-sm md:h-8 md:text-xs"
                        value={row.quantity}
                        onChange={(event) => {
                          const value = sanitizeDecimalInput(event.target.value);
                          if (value !== null) {
                            updateRow(row.id, "quantity", value);
                          }
                        }}
                      />
                    </div>

                    <div className="space-y-1 md:col-span-2 md:space-y-0">
                      {isMobile && (
                        <Label className="text-xs text-muted-foreground md:hidden">
                          {secondQuantityLabel}
                        </Label>
                      )}
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder={usesAmountOnly ? "-" : "0"}
                        className="h-10 text-sm md:h-8 md:text-xs"
                        disabled={usesAmountOnly}
                        value={row.secondQuantity}
                        onChange={(event) => {
                          const value = sanitizeDecimalInput(event.target.value);
                          if (value !== null) {
                            updateRow(row.id, "secondQuantity", value);
                          }
                        }}
                      />
                    </div>

                    <div className="space-y-1 md:col-span-2 md:space-y-0">
                      {isMobile && (
                        <Label className="text-xs text-muted-foreground md:hidden">
                          Fee
                        </Label>
                      )}
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        className="h-10 text-sm md:h-8 md:text-xs"
                        value={row.fee}
                        onChange={(event) => {
                          const value = sanitizeDecimalInput(event.target.value);
                          if (value !== null) {
                            updateRow(row.id, "fee", value);
                          }
                        }}
                      />
                    </div>

                    <div className="space-y-1 md:col-span-3 md:space-y-0">
                      {isMobile && (
                        <Label className="text-xs text-muted-foreground md:hidden">
                          Fee Currency
                        </Label>
                      )}
                      <Select
                        value={row.feeCurrency}
                        onValueChange={(value) =>
                          updateRow(row.id, "feeCurrency", value)
                        }
                      >
                        <SelectTrigger className="h-10 text-sm md:h-8 md:text-xs">
                          <SelectValue placeholder="Currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {fiatOptions.map((fiat) => (
                            <SelectItem
                              key={fiat.asset_id}
                              value={fiat.asset_id}
                            >
                              {fiat.symbol}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex justify-end md:col-span-1 md:justify-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {submitting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing {progress.current}/{progress.total} records...
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid() || submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Transactions
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
